import React from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowUp, MapPin, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function Footer() {
  const { t } = useTranslation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer role="contentinfo" className="py-16 border-t border-border/70 bg-card/40 backdrop-blur-sm">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          {/* Col 1: Brand & Studio */}
          <div className="space-y-4 min-w-0">
            <button 
              onClick={scrollToTop} 
              className="flex items-center gap-2.5 group cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg transition-all"
              aria-label={t('common.back_to_top', 'Flash Web - Retourner en haut de la page')}
            >
              <div className="w-8 h-8 rounded-xl overflow-hidden shadow-md shadow-primary/25 group-hover:scale-105 transition-transform border border-primary/35 bg-zinc-950 flex items-center justify-center p-0.5 shrink-0">
                <img 
                  src="/logo_agency_thumb_64.webp" 
                  alt="Flash Web" 
                  width="32"
                  height="32"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover rounded-lg" 
                />
              </div>
              <span className="font-heading font-extrabold text-xl tracking-tight text-foreground group-hover:text-primary-ink transition-colors">
                Flash Web
              </span>
            </button>
            <p className="text-muted-foreground text-sm leading-relaxed break-words">
              {t('footer.desc', 'Agence digitale experte en création de sites web ultra-performants, applications sur-mesure et stratégies SEO 100% orientées résultats.')}
            </p>
            <div className="pt-1">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted/60 border border-border text-foreground text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {t('footer.available_for_projects', 'Disponibles pour projets')}
              </span>
            </div>
          </div>

          {/* Col 2: Navigation Agence & Pôles Régionaux */}
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-sm text-foreground uppercase tracking-wider mb-4">
              {t('footer.nav', 'Navigation')}
            </h3>
            <nav aria-label={t('footer.nav')} className="flex flex-col gap-2.5 text-sm text-muted-foreground">
              <Link to="/expertises" className="hover:text-primary-ink transition-colors">{t('nav.services', 'Expertises')}</Link>
              <Link to="/audit" className="hover:text-primary-ink transition-colors">{t('nav.audit', 'Audit SEO')}</Link>
              <Link to="/realisations" className="hover:text-primary-ink transition-colors">{t('nav.portfolio', 'Réalisations')}</Link>
              <Link to="/#pricing" className="hover:text-primary-ink transition-colors">{t('nav.pricing', 'Tarifs')}</Link>
              <Link to="/blog" className="hover:text-primary-ink transition-colors">{t('footer.blog', 'Blog')}</Link>
              <Link to="/#faq" className="hover:text-primary-ink transition-colors">{t('nav.faq', 'FAQ')}</Link>
            </nav>
            <div className="mt-4 pt-3 border-t border-border/50">
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider block mb-2">
                Rennes & Bretagne
              </span>
              <nav aria-label="Solutions locales Rennes" className="flex flex-col gap-2 text-xs text-muted-foreground">
                <Link to="/creation-site-internet-rennes" className="hover:text-primary-ink transition-colors">Création de site web</Link>
                <Link to="/creation-site-e-commerce-rennes" className="hover:text-primary-ink transition-colors">Boutique e-commerce</Link>
                <Link to="/refonte-site-internet-rennes" className="hover:text-primary-ink transition-colors">Refonte & Modernisation</Link>
              </nav>
            </div>
          </div>

          {/* Col 3: Outils & Solutions */}
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-sm text-foreground uppercase tracking-wider mb-4">
              {t('nav.tools', 'Outils')}
            </h3>
            <nav aria-label={t('nav.tools')} className="flex flex-col gap-2.5 text-sm text-muted-foreground">
              <Link to="/outils/backlinks" className="hover:text-primary-ink transition-colors">{t('nav.backlinks', 'Backlinks DR')}</Link>
              <Link to="/outils/mots-cles" className="hover:text-primary-ink transition-colors">{t('nav.keywords', 'Explorateur Mots-Clés')}</Link>
              <Link to="/outils/concurrents" className="hover:text-primary-ink transition-colors">{t('nav.competitor_gap', 'Comparateur Concurrence')}</Link>
              <Link to="/composants" className="hover:text-primary-ink transition-colors">{t('nav.components', 'Flash UI')}</Link>
              <Link to="/login" rel="nofollow" className="hover:text-primary-ink transition-colors">{t('nav.client_space', 'Espace Client')}</Link>
              <Link to="/contact" className="hover:text-primary-ink transition-colors">{t('footer.contact', 'Contact direct')}</Link>
            </nav>
          </div>

          {/* Col 4: Légal & Coordonnées */}
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-sm text-foreground uppercase tracking-wider mb-4">
              {t('footer.legal', 'Légal & Studio')}
            </h3>
            <nav aria-label={t('footer.legal')} className="flex flex-col gap-2.5 text-sm text-muted-foreground mb-6">
              <Link to="/mentions-legales" className="hover:text-primary-ink transition-colors">{t('footer.mentions', 'Mentions Légales')}</Link>
              <Link to="/politique-confidentialite" className="hover:text-primary-ink transition-colors">{t('footer.privacy', 'Politique de Confidentialité')}</Link>
              <Link to="/cgv" className="hover:text-primary-ink transition-colors">{t('footer.cgv', 'CGV')}</Link>
              <Link to="/mentions-legales#accessibilite" className="hover:text-primary-ink transition-colors text-xs text-muted-foreground mt-1">
                {t('footer.accessibility', 'Accessibilité : partiellement conforme (RGAA 4.1)')}
              </Link>
            </nav>
            <div className="pt-3 border-t border-border/60 text-xs text-muted-foreground space-y-1.5">
              <p className="font-semibold text-foreground flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-primary-ink shrink-0" />
                15 Place de la Mairie, 35000 Rennes
              </p>
              <p className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-primary-ink shrink-0" />
                <span className="break-all sm:break-normal">contact@flashwebrennes.online</span>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/60 text-muted-foreground text-xs flex flex-col md:flex-row justify-between items-center gap-4">
          <p>© {new Date().getFullYear()} Flash Web. {t('footer.rights', 'Tous droits réservés.')}</p>
          
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <button
              onClick={scrollToTop}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary-ink transition-colors cursor-pointer px-3 py-1.5 rounded-full border border-border/80 hover:border-primary/40 bg-muted/40 hover:bg-muted/70"
              aria-label={t('footer.back_to_top', 'Haut de page')}
              title={t('footer.back_to_top', 'Haut de page')}
            >
              <span>{t('footer.back_to_top', 'Haut de page')}</span>
              <ArrowUp className="w-3 h-3" />
            </button>
            <p className="hidden sm:inline">{t('footer.location', 'Agence basée à Rennes, opérant partout dans le monde.')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
